import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ClickableCard } from '../components/ClickableCard';
import { API, AuthContext } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { GenerateReportButton } from '../components/GenerateReportButton';
import {
  Search, Library, Loader2, Save, RefreshCw, ShieldAlert, Sparkles, UserCheck, X,
  ChevronRight, Settings2, Trash2, Plus, FileDown, Upload, ClipboardList,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const RATING_COLORS = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-slate-100 text-slate-700 border-slate-200',
};
const RISK_STATUS_COLORS = {
  Open:          'bg-red-50 text-red-700 border-red-200',
  'In Progress': 'bg-amber-50 text-amber-800 border-amber-200',
  Mitigated:     'bg-blue-50 text-blue-700 border-blue-200',
  Accepted:      'bg-slate-50 text-slate-600 border-slate-200',
  Transferred:   'bg-purple-50 text-purple-700 border-purple-200',
  Closed:        'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const LEVELS = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];
const RISK_STATUSES = ['Open', 'In Progress', 'Mitigated', 'Accepted', 'Transferred', 'Closed'];
const TREATMENT_TYPES = ['Mitigate', 'Accept', 'Transfer', 'Avoid'];
const TREATMENT_STATUSES = ['Planned', 'In Progress', 'Complete', 'Blocked', 'Cancelled'];

function ProvenanceBadge({ modified }) {
  return modified ? (
    <Badge className="bg-purple-100 text-purple-800 border-purple-200 border text-[10px]" data-testid="risk-provenance-user">
      <UserCheck className="w-3 h-3 mr-1" /> USER MODIFIED
    </Badge>
  ) : (
    <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px]" data-testid="risk-provenance-system">
      <Sparkles className="w-3 h-3 mr-1" /> SYSTEM GENERATED
    </Badge>
  );
}

function StatCard({ label, value, testid, tone = 'slate', active, onClick }) {
  const t = { slate: 'text-slate-900', red: 'text-red-600', orange: 'text-orange-600', amber: 'text-amber-600', purple: 'text-purple-600', blue: 'text-blue-600' };
  return (
    <ClickableCard active={active} onClick={onClick} testid={`${testid}-card`}>
      <Card className="border-slate-200"><CardContent className="py-3 px-4">
        <div className={`text-2xl font-bold ${t[tone]}`} data-testid={testid}>{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </CardContent></Card>
    </ClickableCard>
  );
}

// Enum values documented in the template dropdown menu — keep in sync with
// the backend `_LIKELIHOOD_NORMALIZE / _STATUS_NORMALIZE / …` maps in
// routes/risk_engine.py so users get accurate guidance.
const TEMPLATE_FIELDS = [
  { name: 'Risk Title', required: true, hint: 'Short name of the risk' },
  { name: 'Risk Description', required: false, hint: 'Free-text' },
  { name: 'Likelihood', required: true, values: ['Very High', 'High', 'Medium', 'Low', 'Very Low'] },
  { name: 'Impact', required: true, values: ['Very High', 'High', 'Medium', 'Low', 'Very Low'] },
  { name: 'Status', required: false, values: ['Open', 'In Progress', 'Mitigated', 'Accepted', 'Transferred', 'Closed'] },
  { name: 'Risk Owner', required: false, hint: 'Person or role' },
  { name: 'Risk Treatment Type', required: false, values: ['Mitigate', 'Accept', 'Transfer', 'Avoid'] },
  { name: 'Risk Treatment Status', required: false, values: ['Planned', 'In Progress', 'Complete', 'Blocked', 'Cancelled'] },
  { name: 'Risk Treatment Owner', required: false, hint: 'Person or role' },
  { name: 'Target Date', required: false, hint: 'YYYY-MM-DD' },
  { name: 'Action', required: false, hint: 'Free-text corrective action' },
  { name: 'Residual risk (after treatment)', required: false, hint: 'Free-text' },
];

function TemplateMenu() {
  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/risk-engine/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Risk_Template_v2.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download template');
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="risk-template-btn">
          <ClipboardList className="w-4 h-4 mr-1" /> Template
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px] max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-slate-900">Template columns &amp; expected values</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TEMPLATE_FIELDS.map((f) => (
          <div key={f.name} className="px-2 py-1.5 text-xs" data-testid={`risk-template-field-${f.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-900">{f.name}</span>
              {f.required && <span className="text-[10px] text-red-600 font-semibold">REQUIRED</span>}
            </div>
            {f.values ? (
              <div className="mt-0.5 flex flex-wrap gap-1">
                {f.values.map((v) => (
                  <span key={v} className="inline-block px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 text-[10px]">
                    {v}
                  </span>
                ))}
              </div>
            ) : (
              <div className="mt-0.5 text-slate-500">{f.hint}</div>
            )}
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={downloadTemplate} className="cursor-pointer text-teal-700 font-medium" data-testid="risk-template-download-btn">
          <FileDown className="w-4 h-4 mr-2" /> Download Excel template
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ImportButton({ canWrite, onImported }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/risk-engine/import`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { created = 0, skipped = 0, errors = [] } = r.data || {};
      if (created && !skipped) toast.success(`Imported ${created} risk${created === 1 ? '' : 's'}`);
      else if (created && skipped) toast.warning(`Imported ${created}, skipped ${skipped} — check row errors below`);
      else if (skipped) toast.error(`No rows imported — ${skipped} skipped`);
      else toast.info('No rows found to import');
      if (errors.length) {
        console.warn('Risk import row errors:', errors);
        const preview = errors.slice(0, 3).map((e) => `Row ${e.row}: ${e.errors.join(', ')}`).join('\n');
        toast.error(preview, { duration: 8000 });
      }
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };
  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xlsm"
        className="hidden"
        onChange={handleFile}
        data-testid="risk-import-input"
      />
      <Button
        variant="outline"
        disabled={!canWrite || uploading}
        onClick={() => fileRef.current?.click()}
        data-testid="risk-import-btn"
      >
        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
        {uploading ? 'Importing…' : 'Import'}
      </Button>
    </>
  );
}


function EditDialog({ open, onOpenChange, risk, canWrite, onSaved, treatmentFlag }) {
  const [state, setState] = useState({});
  const [treatment, setTreatment] = useState(null);
  const [treatState, setTreatState] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!risk) return;
    setState({
      title: risk.title || '', description: risk.description || '',
      likelihood: risk.likelihood || 'Medium', impact: risk.impact || 'Medium',
      owner: risk.owner || '', status: risk.status || 'Open',
      notes: risk.notes || '',
    });
    (async () => {
      if (!treatmentFlag) { setTreatment(null); return; }
      try {
        const t = await axios.get(`${API}/risk-engine/treatments/${risk.control_id}`);
        setTreatment(t.data);
        setTreatState({
          treatment_type: t.data.treatment_type || 'Mitigate',
          actions: t.data.actions || '',
          target_date: t.data.target_date || '',
          owner: t.data.owner || '',
          status: t.data.status || 'Planned',
          residual_risk: t.data.residual_risk || '',
        });
      } catch { /* ignore */ }
    })();
  }, [risk, treatmentFlag]);

  if (!risk) return null;

  const saveAll = async () => {
    setSaving(true);
    try {
      const results = await Promise.all([
        axios.patch(`${API}/risk-engine/risks/${risk.control_id}`, state),
        treatmentFlag && treatment
          ? axios.patch(`${API}/risk-engine/treatments/${risk.control_id}`, treatState)
          : Promise.resolve(null),
      ]);
      const riskRes = results[0];
      const treatRes = results[1];
      if (treatRes) setTreatment(treatRes.data);
      toast.success('Changes saved. Your edits are protected from regeneration.');
      onSaved?.(riskRes.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const regenerate = async (kind) => {
    setSaving(true);
    try {
      const url = kind === 'risk'
        ? `${API}/risk-engine/risks/${risk.control_id}/regenerate`
        : `${API}/risk-engine/treatments/${risk.control_id}/regenerate`;
      const res = await axios.post(url);
      if (kind === 'risk') { onSaved?.(res.data); }
      else { setTreatment(res.data); setTreatState({
        treatment_type: res.data.treatment_type || 'Mitigate',
        actions: res.data.actions || '', target_date: res.data.target_date || '',
        owner: res.data.owner || '', status: res.data.status || 'Planned',
        residual_risk: res.data.residual_risk || '',
      });}
      toast.success('System snapshot refreshed — your overrides preserved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regeneration failed');
    } finally { setSaving(false); }
  };

  const dismiss = async () => {
    if (!window.confirm('Dismiss this risk? You can re-enable later.')) return;
    try {
      await axios.delete(`${API}/risk-engine/risks/${risk.control_id}`);
      toast.success('Risk dismissed');
      onSaved?.({ ...risk, is_deleted: true });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dismiss failed');
    }
  };

  const snap = risk.system_snapshot || {};
  const overrides = risk.user_override || {};
  const overriddenKeys = new Set(Object.keys(overrides).filter((k) => overrides[k] != null));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="risk-edit-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="font-mono text-slate-500 text-base">{risk.control_id}</span>
            <span>{risk.title || risk.control_name}</span>
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1" asChild>
            <div>
              <ProvenanceBadge modified={risk.is_user_modified} />
              <span className="text-xs text-slate-500">Priority: {risk.control_priority || '—'}</span>
              <span className="text-xs text-slate-500">Gap: {risk.gap_reason || '—'}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          <strong>Auto-suggested from the current assessment.</strong> Any field you edit here is stored as a
          USER OVERRIDE and will NEVER be overwritten by <em>Regenerate</em>. Overridden fields are marked with a purple dot.
        </div>

        {/* Affected frameworks */}
        {risk.affected_frameworks?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {risk.affected_frameworks.map((f) => (
              <span
                key={f.framework.id}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border"
                style={{ borderColor: f.framework.color, color: f.framework.color, backgroundColor: `${f.framework.color}14` }}
                title={(f.codes || []).join(', ')}
              >
                {f.framework.short_name} · {(f.codes || []).join(', ')}
              </span>
            ))}
          </div>
        )}

        {/* Risk section */}
        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-900">Risk</h3>
            <Button size="sm" variant="ghost" onClick={() => regenerate('risk')} disabled={saving} data-testid="risk-regenerate-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate snapshot
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <FieldWithProvenance label="Title" overridden={overriddenKeys.has('title')} systemValue={snap.title}>
              <Input value={state.title} onChange={(e) => setState({ ...state, title: e.target.value })} disabled={!canWrite} data-testid="risk-title-input" />
            </FieldWithProvenance>
            <FieldWithProvenance label="Owner" overridden={overriddenKeys.has('owner')} systemValue={snap.owner}>
              <Input value={state.owner} onChange={(e) => setState({ ...state, owner: e.target.value })} disabled={!canWrite} data-testid="risk-owner-input" />
            </FieldWithProvenance>
            <FieldWithProvenance label="Likelihood" overridden={overriddenKeys.has('likelihood')} systemValue={snap.likelihood}>
              <Select value={state.likelihood} onValueChange={(v) => setState({ ...state, likelihood: v })} disabled={!canWrite}>
                <SelectTrigger data-testid="risk-likelihood-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWithProvenance>
            <FieldWithProvenance label="Impact" overridden={overriddenKeys.has('impact')} systemValue={snap.impact}>
              <Select value={state.impact} onValueChange={(v) => setState({ ...state, impact: v })} disabled={!canWrite}>
                <SelectTrigger data-testid="risk-impact-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LEVELS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWithProvenance>
            <FieldWithProvenance label="Rating (auto)" overridden={overriddenKeys.has('risk_rating')} systemValue={snap.risk_rating}>
              <Input value={risk.risk_rating || ''} disabled className="font-semibold" />
            </FieldWithProvenance>
            <FieldWithProvenance label="Status" overridden={overriddenKeys.has('status')} systemValue={snap.status}>
              <Select value={state.status} onValueChange={(v) => setState({ ...state, status: v })} disabled={!canWrite}>
                <SelectTrigger data-testid="risk-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RISK_STATUSES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </FieldWithProvenance>
          </div>

          <FieldWithProvenance label="Description" overridden={overriddenKeys.has('description')} systemValue={snap.description}>
            <Textarea rows={4} value={state.description} onChange={(e) => setState({ ...state, description: e.target.value })} disabled={!canWrite} data-testid="risk-description-input" />
          </FieldWithProvenance>

          <div className="flex justify-start items-center mt-3">
            <Button variant="ghost" onClick={dismiss} disabled={!canWrite} data-testid="risk-dismiss-btn">
              <Trash2 className="w-4 h-4 mr-1 text-red-500" /> Dismiss risk
            </Button>
          </div>
        </section>

        {/* Treatment section */}
        {treatmentFlag ? (
          treatment ? (
            <section className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="treatment-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  Risk Treatment Plan <ProvenanceBadge modified={treatment.is_user_modified} />
                </h3>
                <Button size="sm" variant="ghost" onClick={() => regenerate('treatment')} disabled={saving} data-testid="treatment-regenerate-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate snapshot
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Treatment type</Label>
                  <Select value={treatState.treatment_type} onValueChange={(v) => setTreatState({ ...treatState, treatment_type: v })} disabled={!canWrite}>
                    <SelectTrigger data-testid="treatment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TREATMENT_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={treatState.status} onValueChange={(v) => setTreatState({ ...treatState, status: v })} disabled={!canWrite}>
                    <SelectTrigger data-testid="treatment-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TREATMENT_STATUSES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Owner</Label>
                  <Input value={treatState.owner} onChange={(e) => setTreatState({ ...treatState, owner: e.target.value })} disabled={!canWrite} data-testid="treatment-owner-input" />
                </div>
                <div>
                  <Label>Due date</Label>
                  <Input type="date" value={treatState.target_date} onChange={(e) => setTreatState({ ...treatState, target_date: e.target.value })} disabled={!canWrite} data-testid="treatment-target-date-input" />
                </div>
              </div>
              <div className="mt-3">
                <Label>Actions</Label>
                <Textarea rows={4} value={treatState.actions} onChange={(e) => setTreatState({ ...treatState, actions: e.target.value })} disabled={!canWrite} data-testid="treatment-actions-input" />
              </div>
              <div className="mt-3">
                <Label>Residual risk (after treatment)</Label>
                <Textarea rows={2} value={treatState.residual_risk} onChange={(e) => setTreatState({ ...treatState, residual_risk: e.target.value })} disabled={!canWrite} />
              </div>
            </section>
          ) : null
        ) : (
          <section className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>Risk Treatment Engine is OFF.</strong> Enable it in Settings to auto-suggest a treatment plan.
          </section>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t border-slate-200">
          <Button onClick={saveAll} disabled={saving || !canWrite} data-testid="risk-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <label className="text-xs font-semibold uppercase text-slate-500 tracking-wide">{children}</label>;
}

function FieldWithProvenance({ label, overridden, systemValue, children }) {
  return (
    <div className="mt-1">
      <div className="flex items-center gap-1">
        <Label>{label}</Label>
        {overridden && (
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500" title={`System suggested: ${systemValue || '—'}`} />
        )}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function RiskEnginePage() {
  const { user } = useContext(AuthContext);
  const canWrite = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'client' || user?.role === 'auditor';
  const canSettings = user?.role === 'admin' || user?.role === 'super_admin';
  const [flags, setFlags] = useState({ use_new_risk_engine: true, use_new_risk_treatment_engine: true });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [rating, setRating] = useState(() => searchParams.get('rating') || 'all');
  const [status, setStatus] = useState(() => searchParams.get('status') || 'all');
  const pickCard = (r, st) => {
    const same = rating === r && status === st;
    setRating(same ? 'all' : r);
    setStatus(same ? 'all' : st);
  };
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsState, setSettingsState] = useState({ use_new_risk_engine: true, use_new_risk_treatment_engine: true });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadFlags = useCallback(async () => {
    const r = await axios.get(`${API}/risk-engine/settings`);
    setFlags(r.data);
    setSettingsState(r.data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (rating !== 'all') params.set('rating', rating);
      if (status !== 'all') params.set('status', status);
      if (includeDismissed) params.set('include_dismissed', 'true');
      const r = await axios.get(`${API}/risk-engine/risks?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load risks');
    } finally { setLoading(false); }
  }, [search, rating, status, includeDismissed]);

  useEffect(() => { loadFlags(); }, [loadFlags]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await axios.patch(`${API}/risk-engine/settings`, settingsState);
      setFlags(r.data);
      toast.success('Risk engine settings saved');
      setSettingsOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally { setSavingSettings(false); }
  };

  const openRisk = (r) => { setSelectedRisk(r); setDialogOpen(true); };
  const onRiskSaved = () => { load(); };

  const s = data?.summary || {};

  if (data && data.feature_enabled === false) {
    return (
      <div className="space-y-4" data-testid="risk-engine-page">
        <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-red-600" /> Risk Register
        </h1>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900 flex items-center justify-between gap-3">
            <span><strong>Risk Register is OFF.</strong> Enable it in Settings.</span>
            {canSettings && (
              <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="risk-settings-btn">
                <Settings2 className="w-4 h-4 mr-1" /> Settings
              </Button>
            )}
          </CardContent>
        </Card>
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen}
          state={settingsState} setState={setSettingsState} onSave={saveSettings} saving={savingSettings} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="risk-engine-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Library className="w-4 h-4" /><span>Compliance Engine v2</span>
            <span className="text-slate-300">/</span><span>Risk Register</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-red-600" /> Risk Register
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Suggested from your gap assessment. One risk per underlying control gap, with all affected frameworks visible.
            Your manual edits are preserved through regeneration.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TemplateMenu />
          <ImportButton canWrite={canWrite} onImported={load} />
          <Button
            variant="default"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={() => setAddOpen(true)}
            disabled={!canWrite}
            data-testid="risk-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Risk
          </Button>
          <GenerateReportButton
            options={[
              { key: 'risk_register', label: 'Risk Register (PDF)', report_type: 'risk_register' },
              { key: 'risk_treatment_plan', label: 'Risk Treatment Plan (PDF)', report_type: 'risk_treatment_plan' },
            ]}
            variant="default"
            className="bg-orange-600 hover:bg-orange-700 text-white"
            testid="risk-generate-report-btn"
          />
          {canSettings && (
            <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="risk-settings-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Settings
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Risk Rating</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Total" value={s.total ?? 0} testid="risk-total" active={rating === 'all' && status === 'all'} onClick={() => pickCard('all', 'all')} />
            {[['Critical', 'red'], ['High', 'orange'], ['Medium', 'amber'], ['Low', 'slate']].map(([r, tone]) => (
              <StatCard key={r} label={r} value={s.by_rating?.[r] ?? 0} tone={tone} testid={`risk-${r.toLowerCase()}`}
                active={rating === r && status === 'all'} onClick={() => pickCard(r, 'all')} />
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Risk Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {RISK_STATUSES.map((st) => (
              <StatCard
                key={st}
                label={st}
                value={s.by_status?.[st] ?? 0}
                tone={st === 'Open' ? 'red' : st === 'In Progress' ? 'amber' : st === 'Closed' ? 'slate' : 'slate'}
                testid={`risk-status-${st.toLowerCase().replace(/\s+/g, '-')}`}
                active={status === st && rating === 'all'}
                onClick={() => pickCard('all', st)}
              />
            ))}
          </div>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="risk-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title, control, owner…" className="pl-9" />
          </div>
          <Select value={rating} onValueChange={setRating}>
            <SelectTrigger data-testid="risk-filter-rating" className="w-[160px]"><SelectValue placeholder="Rating" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              {['Critical', 'High', 'Medium', 'Low'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="risk-filter-status" className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {RISK_STATUSES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={includeDismissed ? 'default' : 'outline'} onClick={() => setIncludeDismissed(!includeDismissed)} data-testid="risk-toggle-dismissed">
            {includeDismissed ? 'Showing dismissed' : 'Hide dismissed'}
          </Button>
          {(search || rating !== 'all' || status !== 'all') && (
            <Button variant="ghost" onClick={() => { setSearch(''); setRating('all'); setStatus('all'); }} data-testid="risk-clear-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200"><CardContent className="p-0"><div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead>Risk</TableHead>
            <TableHead>Rating</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead>Residual Risk</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading risks…</TableCell></TableRow>
            ) : (data?.risks || []).length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">No risks match the current filters.</TableCell></TableRow>
            ) : (
              data.risks.map((r, idx) => (
                <TableRow key={r.control_id} className={`cursor-pointer hover:bg-slate-50 ${r.is_deleted ? 'opacity-50' : ''}`} onClick={() => openRisk(r)} data-testid={`risk-row-${r.control_id}`}>
                  <TableCell className="font-mono text-slate-500 text-sm">{idx + 1}</TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900 max-w-md">{r.title}</div>
                    {r.description && (
                      <div className="text-xs text-slate-500 mt-0.5 max-w-md line-clamp-2" data-testid={`risk-description-${r.control_id}`}>
                        {r.description}
                      </div>
                    )}
                    {r.is_custom && (
                      <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                        <Plus className="w-2.5 h-2.5" /> custom
                      </span>
                    )}
                  </TableCell>
                  <TableCell><Badge className={`${RATING_COLORS[r.risk_rating] || ''} border text-xs`}>{r.risk_rating || '—'}</Badge></TableCell>
                  <TableCell>
                    <Badge className={`${RISK_STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-700 border-slate-200'} border text-xs`} data-testid={`risk-status-badge-${r.control_id}`}>
                      {r.status}
                    </Badge>
                    {r.closed_by_system && (
                      <div className="text-[10px] text-emerald-600 mt-0.5 inline-flex items-center gap-0.5" data-testid={`risk-auto-closed-${r.control_id}`}>
                        <Sparkles className="w-2.5 h-2.5" /> auto-closed
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-700" data-testid={`risk-owner-${r.control_id}`}>
                    {r.owner || <span className="text-slate-400 italic">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-700 max-w-xs" data-testid={`risk-residual-${r.control_id}`}>
                    {r.residual_risk
                      ? <div className="line-clamp-2" title={r.residual_risk}>{r.residual_risk}</div>
                      : <span className="text-slate-400 italic">—</span>}
                  </TableCell>
                  <TableCell className="text-xs text-slate-700" data-testid={`risk-due-date-${r.control_id}`}>
                    {r.target_date ? r.target_date.slice(0, 10) : <span className="text-slate-400 italic">—</span>}
                  </TableCell>
                  <TableCell><ChevronRight className="w-4 h-4 text-slate-400" /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div></CardContent></Card>

      <EditDialog open={dialogOpen} onOpenChange={setDialogOpen} risk={selectedRisk} canWrite={canWrite} onSaved={onRiskSaved} treatmentFlag={flags.use_new_risk_treatment_engine} />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen}
        state={settingsState} setState={setSettingsState} onSave={saveSettings} saving={savingSettings} />
      <AddRiskDialog open={addOpen} onOpenChange={setAddOpen} onCreated={() => load()} />
    </div>
  );
}

function AddRiskDialog({ open, onOpenChange, onCreated }) {
  const empty = {
    title: '', description: '', likelihood: 'Medium', impact: 'Medium',
    owner: '', status: 'Open', notes: '',
    treatment_type: 'Mitigate', actions: '', due_date: '', residual_risk: '',
  };
  const [state, setState] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setState(empty); /* eslint-disable-next-line */ }, [open]);

  const create = async () => {
    if (!state.title.trim()) { toast.error('Title is required'); return; }
    if (!state.due_date) { toast.error('Due Date is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/risk-engine/risks`, state);
      toast.success('Risk added to register');
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create risk');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-risk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-red-600" /> Add custom risk</DialogTitle>
          <DialogDescription>Create a standalone risk and capture its treatment plan at the same time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-3">
          <div>
            <Label>Title *</Label>
            <Input value={state.title} onChange={(e) => setState({ ...state, title: e.target.value })} placeholder="Short risk title" data-testid="add-risk-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={state.description} onChange={(e) => setState({ ...state, description: e.target.value })} placeholder="What could go wrong?" data-testid="add-risk-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Likelihood</Label>
              <Select value={state.likelihood} onValueChange={(v) => setState({ ...state, likelihood: v })}>
                <SelectTrigger data-testid="add-risk-likelihood"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Very Low', 'Low', 'Medium', 'High', 'Very High'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Impact</Label>
              <Select value={state.impact} onValueChange={(v) => setState({ ...state, impact: v })}>
                <SelectTrigger data-testid="add-risk-impact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Very Low', 'Low', 'Medium', 'High', 'Very High'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Owner</Label>
              <Input value={state.owner} onChange={(e) => setState({ ...state, owner: e.target.value })} placeholder="Assignee" data-testid="add-risk-owner" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={state.status} onValueChange={(v) => setState({ ...state, status: v })}>
                <SelectTrigger data-testid="add-risk-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RISK_STATUSES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-3 mt-2">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Treatment Plan</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Treatment type</Label>
                <Select value={state.treatment_type} onValueChange={(v) => setState({ ...state, treatment_type: v })}>
                  <SelectTrigger data-testid="add-risk-treatment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Mitigate', 'Accept', 'Transfer', 'Avoid'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input type="date" value={state.due_date} onChange={(e) => setState({ ...state, due_date: e.target.value })} data-testid="add-risk-due-date" />
              </div>
            </div>
            <div className="mt-3">
              <Label>Actions</Label>
              <Textarea rows={3} value={state.actions} onChange={(e) => setState({ ...state, actions: e.target.value })} placeholder="How will this risk be treated?" data-testid="add-risk-actions" />
            </div>
            <div className="mt-3">
              <Label>Residual risk (after treatment)</Label>
              <Textarea rows={2} value={state.residual_risk} onChange={(e) => setState({ ...state, residual_risk: e.target.value })} placeholder="What risk remains after the controls are applied?" data-testid="add-risk-residual" />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={state.notes} onChange={(e) => setState({ ...state, notes: e.target.value })} placeholder="Optional context" data-testid="add-risk-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={saving} data-testid="add-risk-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Add risk
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ open, onOpenChange, state, setState, onSave, saving }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="risk-settings-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Risk Register settings</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-3">
          <ToggleRow label="Use Risk Register"
            description="Auto-suggest risks from control gaps."
            checked={state.use_new_risk_engine}
            onChange={(v) => setState({ ...state, use_new_risk_engine: v })}
            testid="risk-engine-toggle" />
          <ToggleRow label="Use Risk Treatment Plan"
            description="Auto-suggest treatment plans from Master Control recommendations."
            checked={state.use_new_risk_treatment_engine}
            onChange={(v) => setState({ ...state, use_new_risk_treatment_engine: v })}
            testid="treatment-engine-toggle" />
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="risk-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, description, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
      <div className="pr-4">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
